import api from "./base-api"

// ──────────────────────────────────────────────────────────────────────────
//  Experience (XP) RTK Query slice.
//
//  USE_MOCKS=true — fully standalone mode without a backend. All requests go
//  through queryFn and return the fixtures below. To switch to the live
//  backend: set USE_MOCKS=false — routes and response formats match
//  docs/features/experience-xp/api-contract.md.
// ──────────────────────────────────────────────────────────────────────────

// Mocks are controlled by the REACT_APP_USE_XP_MOCKS env variable.
// Defaults: dev=true, prod=false. For a local integration run against
// the live backend, start with `REACT_APP_USE_XP_MOCKS=false npm start`.
const USE_MOCKS =  false //(process.env.REACT_APP_USE_XP_MOCKS ?? (process.env.NODE_ENV === "production" ? "false" : "true")) === "true"

// ── Mocks (remove/do not use when USE_MOCKS=false) ─────────────────────────

const MOCK_LEVELS = [
    { id: "lvl1", level: 1, minXp: 100, rewardBelcoins: 1 },
    { id: "lvl2", level: 2, minXp: 200, rewardBelcoins: 2 },
    { id: "lvl3", level: 3, minXp: 400, rewardBelcoins: 4 },
    { id: "lvl4", level: 4, minXp: 800, rewardBelcoins: 8 },
]

const MOCK_CATEGORIES = [
    { id: "cat-en", name: "english", description: "English club, practice", color: "#03907F", active: true },
    { id: "cat-lr", name: "learning", description: "Lectures, meetups, education", color: "#F05125", active: true },
    { id: "cat-tb", name: "teambuild", description: "Team building and quests", color: "#92278F", active: true },
    { id: "cat-ev", name: "events", description: "Offline events in the offices", color: "#2F6FCC", active: true },
]

const MOCK_ACHIEVEMENT_CATEGORIES = [
    // Shields — six totalXp milestones rendered as PNG images.
    { id: "ach-100", name: "100 XP", description: "Shield for 100 XP", imageUrl: "/achievements/Achievement_100_color_500x500.png", xpCategoryId: null, xpThreshold: 100 },
    { id: "ach-200", name: "200 XP", description: "Shield for 200 XP", imageUrl: "/achievements/Achievement_200_color_500x500.png", xpCategoryId: null, xpThreshold: 200 },
    { id: "ach-300", name: "300 XP", description: "Shield for 300 XP", imageUrl: "/achievements/Achievement_300_color_500x500.png", xpCategoryId: null, xpThreshold: 300 },
    { id: "ach-400", name: "400 XP", description: "Shield for 400 XP", imageUrl: "/achievements/Achievement_400_color_500x500.png", xpCategoryId: null, xpThreshold: 400 },
    { id: "ach-500", name: "500 XP", description: "Shield for 500 XP", imageUrl: "/achievements/Achievement_500_color_500x500.png", xpCategoryId: null, xpThreshold: 500 },
    { id: "ach-exp", name: "Expert", description: "Expert shield (600 XP)", imageUrl: "/achievements/Achievement_Expert_color_500x500.png", xpCategoryId: null, xpThreshold: 600 },
]

const mockState = {
    totalXp: 345,
    categoryXp: { english: 120, learning: 150, teambuild: 75, events: 0 },
    lastClaimedLevel: 2,
    transactions: [
        { id: "tx1", email: "me@belka.team", delta: 50, type: "ACCRUAL", categoryId: "cat-en", categoryName: "english", sourceRequestId: "req1", rewardLevel: null, comment: "English club 14.04", authorEmail: "organizer@belka.team", authorName: "Olga Ivanova", createdAt: "2026-04-14T12:00:00" },
        { id: "tx2", email: "me@belka.team", delta: 100, type: "ACCRUAL", categoryId: "cat-lr", categoryName: "learning", sourceRequestId: "req2", rewardLevel: null, comment: "Gamedev meetup", authorEmail: "organizer@belka.team", authorName: "Olga Ivanova", createdAt: "2026-04-10T09:00:00" },
        { id: "tx3", email: "me@belka.team", delta: 0, type: "REWARD_CLAIM", categoryId: null, categoryName: null, sourceRequestId: null, rewardLevel: 1, comment: "XP reward, level 1", authorEmail: "SYSTEM", authorName: "SYSTEM", createdAt: "2026-04-08T10:00:00" },
        { id: "tx4", email: "me@belka.team", delta: 0, type: "REWARD_CLAIM", categoryId: null, categoryName: null, sourceRequestId: null, rewardLevel: 2, comment: "XP reward, level 2", authorEmail: "SYSTEM", authorName: "SYSTEM", createdAt: "2026-04-09T10:00:00" },
    ],
    achievements: [
        { id: "a1", email: "me@belka.team", achievementCategoryId: "ach-bard", achievementCategoryName: "Brave Bard", imageUrl: "", grantedAt: "2026-04-10T09:30:00", grantedBy: "SYSTEM", grantReason: "AUTO", revokedAt: null, revokedBy: null, revokeReason: null },
    ],
    requests: [
        { id: "req1", authorEmail: "me@belka.team", authorName: "Me Me", categoryId: "cat-en", categoryName: "english", comment: "English club 14.04", items: [{ email: "ivan@belka.team", fullName: "Ivanov Ivan", xp: 50 }, { email: "petr@belka.team", fullName: "Petrov Petr", xp: 30 }], status: "APPROVED", createdAt: "2026-04-14T11:30:00", adminEmail: "admin@belka.team", adminName: "Admin", adminComment: "ok", approvedAt: "2026-04-14T11:55:00" },
        { id: "req3", authorEmail: "me@belka.team", authorName: "Me Me", categoryId: "cat-tb", categoryName: "teambuild", comment: "Quest 20.04", items: [{ email: "anna@belka.team", fullName: "Anna A", xp: 25 }], status: "WAITING", createdAt: "2026-04-20T14:00:00", adminEmail: null, adminName: null, adminComment: null, approvedAt: null },
    ],
}

function computeCurrentLevel(totalXp, levels) {
    let current = 0
    for (const l of levels) {
        if (totalXp >= l.minXp) current = l.level
    }
    return current
}

function buildSnapshot(email) {
    const levels = MOCK_LEVELS
    const totalXp = mockState.totalXp
    const currentLevel = computeCurrentLevel(totalXp, levels)
    const lastClaimedLevel = mockState.lastClaimedLevel
    const pendingRewardBelcoins = levels
        .filter(l => l.level > lastClaimedLevel && l.level <= currentLevel)
        .reduce((s, l) => s + l.rewardBelcoins, 0)
    const nextLevel = levels.find(l => l.level === currentLevel + 1) || null
    const currentLevelObj = levels.find(l => l.level === currentLevel)
    const currentMin = currentLevelObj ? currentLevelObj.minXp : 0
    const xpToNextLevel = nextLevel ? Math.max(0, nextLevel.minXp - totalXp) : 0
    const progressPercent = nextLevel
        ? Math.max(0, Math.min(1, (totalXp - currentMin) / (nextLevel.minXp - currentMin)))
        : null
    return {
        email: email || "me@belka.team",
        totalXp,
        categoryXp: { ...mockState.categoryXp },
        currentLevel,
        lastClaimedLevel,
        pendingRewardBelcoins,
        nextLevel,
        xpToNextLevel,
        progressPercent,
    }
}

function mockDelay(data, ms = 250) {
    return new Promise(resolve => setTimeout(() => resolve({ data }), ms))
}

function m(fn) {
    return { queryFn: async (arg) => fn(arg) }
}

// ── Slice ──────────────────────────────────────────────────────────────────

export const experienceApi = api.injectEndpoints({
    endpoints: (build) => ({
        // ── USER queries ───────────────────────────────────────────────────
        getExperience: build.query({
            ...(USE_MOCKS
                ? m(() => mockDelay(buildSnapshot()))
                : { query: () => "experience" }),
            providesTags: ["Experience"],
        }),
        getMyExperienceTransactions: build.query({
            ...(USE_MOCKS
                ? m(() => mockDelay([...mockState.transactions].sort((a, b) => b.createdAt.localeCompare(a.createdAt))))
                : { query: () => "experience/transactions" }),
            providesTags: ["Experience"],
        }),
        getMyAchievements: build.query({
            ...(USE_MOCKS
                ? m(() => mockDelay(mockState.achievements.filter(a => a.revokedAt === null)))
                : { query: () => "experience/achievements" }),
            providesTags: ["Achievements"],
        }),
        // Каталог щитов страницы XP — бэкенд отдаёт только активный сезон.
        getAllAchievementCategories: build.query({
            ...(USE_MOCKS
                ? m(() => mockDelay(MOCK_ACHIEVEMENT_CATEGORIES))
                : { query: () => "experience/achievements/all" }),
            providesTags: ["AchievementCategories"],
        }),
        // Полный список категорий ачивок по всем сезонам — для админки.
        getAchievementCategoriesAdmin: build.query({
            query: () => "experience/achievements/categories",
            providesTags: ["AchievementCategories"],
        }),

        // Mascots — informational cards on My Experience page (CRUD via admin).
        getMascots: build.query({
            ...(USE_MOCKS
                ? m(() => mockDelay([
                    { id: "m1", slug: "guild-leader", title: "Guild Leader", description: "The main (after you!) hero of the English Battle Pass. If you see this squirrel, an important announcement is in front of you — read it carefully.", imageUrl: "/mascotes/Guild-Leader_1_color_500x500.png", sortOrder: 1, active: true },
                    { id: "m2", slug: "scroll-mage", title: "Scroll Mage", description: "An introvert mage and mentor of all English Battle Pass newcomers. If you see this squirrel, a written English challenge awaits you.", imageUrl: "/mascotes/Scroll-Mage_1_color_500x500.png", sortOrder: 2, active: true },
                    { id: "m3", slug: "brave-bard", title: "Brave Bard", description: "A bard whose music makes the fear of speaking evaporate! If you see this squirrel, a speaking or listening challenge in English awaits you.", imageUrl: "/mascotes/Brave-Bard_1_color_500x500.png", sortOrder: 3, active: true },
                    { id: "m4", slug: "trickster", title: "Trickster", description: "An extrovert jester, patron of the most upbeat and daring English Battle Pass participants. If you see this squirrel, an offline challenge in using English awaits you.", imageUrl: "/mascotes/Trickster_1_color_500x500.png", sortOrder: 4, active: true },
                ]))
                : { query: () => "experience/mascots" }),
            providesTags: ["Mascots"],
        }),
        getAllMascots: build.query({
            ...(USE_MOCKS
                ? m(() => mockDelay([]))
                : { query: () => "experience/mascots/all" }),
            providesTags: ["Mascots"],
        }),
        createMascot: build.mutation({
            ...(USE_MOCKS
                ? m((body) => mockDelay({ id: "m_" + Date.now(), ...body }))
                : { query: (body) => ({ url: "experience/mascots", method: "POST", body }) }),
            invalidatesTags: ["Mascots"],
        }),
        updateMascot: build.mutation({
            ...(USE_MOCKS
                ? m((body) => mockDelay({ ...body }))
                : { query: ({ id, ...body }) => ({ url: `experience/mascots/${id}`, method: "PUT", body }) }),
            invalidatesTags: ["Mascots"],
        }),
        deleteMascot: build.mutation({
            ...(USE_MOCKS
                ? m(() => mockDelay({ status: "ok" }))
                : { query: (id) => ({ url: `experience/mascots/${id}`, method: "DELETE" }) }),
            invalidatesTags: ["Mascots"],
        }),

        // XP coupons — admin CRUD + user redeem.
        getAllCoupons: build.query({
            ...(USE_MOCKS
                ? m(() => mockDelay([]))
                : { query: () => "experience/coupons" }),
            providesTags: ["Coupons"],
        }),
        createCoupon: build.mutation({
            ...(USE_MOCKS
                ? m((body) => mockDelay({ id: "c_" + Date.now(), redemptionCount: 0, ...body }))
                : { query: (body) => ({ url: "experience/coupons", method: "POST", body }) }),
            invalidatesTags: ["Coupons"],
        }),
        updateCoupon: build.mutation({
            ...(USE_MOCKS
                ? m((body) => mockDelay({ ...body }))
                : { query: ({ id, ...body }) => ({ url: `experience/coupons/${id}`, method: "PUT", body }) }),
            invalidatesTags: ["Coupons"],
        }),
        toggleCouponActive: build.mutation({
            ...(USE_MOCKS
                ? m(({ id, active }) => mockDelay({ id, active }))
                : { query: ({ id, active }) => ({
                    url: `experience/coupons/${id}/active`,
                    method: "POST",
                    params: { active },
                }) }),
            invalidatesTags: ["Coupons"],
        }),
        redeemCoupon: build.mutation({
            ...(USE_MOCKS
                ? m((alias) => mockDelay({ alias, title: "Mock coupon", xpAmount: 50 }))
                : { query: (alias) => ({
                    url: `experience/coupons/redeem/${encodeURIComponent(alias)}`,
                    method: "POST",
                }) }),
            invalidatesTags: ["Experience"],
        }),

        // App copy — generic editable text snippets keyed by string id
        // (e.g. "experience.header"). Read-by-key for any user, write for admin.
        getAppCopy: build.query({
            ...(USE_MOCKS
                ? m((key) => mockDelay({ key, title: "", body: "" }))
                : { query: (key) => `copy/${encodeURIComponent(key)}` }),
            providesTags: (result, error, key) => [{ type: "AppCopy", id: key }],
        }),
        getAllAppCopy: build.query({
            ...(USE_MOCKS
                ? m(() => mockDelay([
                    {
                        key: "experience.header",
                        title: "My Experience",
                        body: "<p>Earn <b>XP</b> for activities and claim Belcoin for every level. See the <a href='/rules/belcoins'>rules</a> for details.</p>",
                        updatedAt: new Date().toISOString(),
                    },
                ]))
                : { query: () => "copy" }),
            providesTags: ["AppCopy"],
        }),
        upsertAppCopy: build.mutation({
            ...(USE_MOCKS
                ? m(({ key, ...body }) => mockDelay({ key, ...body }))
                : { query: ({ key, ...body }) => ({
                    url: `copy/${encodeURIComponent(key)}`,
                    method: "PUT",
                    body,
                }) }),
            invalidatesTags: (result, error, arg) => [
                { type: "AppCopy", id: arg.key },
                "AppCopy",
            ],
        }),
        deleteAppCopy: build.mutation({
            ...(USE_MOCKS
                ? m(() => mockDelay({ status: "ok" }))
                : { query: (key) => ({ url: `copy/${encodeURIComponent(key)}`, method: "DELETE" }) }),
            invalidatesTags: (result, error, key) => [
                { type: "AppCopy", id: key },
                "AppCopy",
            ],
        }),
        getExperienceCategories: build.query({
            ...(USE_MOCKS
                ? m(() => mockDelay(MOCK_CATEGORIES.filter(c => c.active)))
                : { query: () => "experience/categories" }),
            providesTags: ["ExperienceCategories"],
        }),
        getExperienceLevels: build.query({
            ...(USE_MOCKS
                ? m(() => mockDelay([...MOCK_LEVELS].sort((a, b) => a.level - b.level)))
                : { query: () => "experience/levels" }),
            providesTags: ["ExperienceLevels"],
        }),
        getMyExperienceRequests: build.query({
            ...(USE_MOCKS
                ? m(() => mockDelay(mockState.requests.filter(r => r.authorEmail === "me@belka.team")))
                : { query: () => "experience/requests/my" }),
            providesTags: ["ExperienceRequests"],
        }),

        // ── USER mutations ─────────────────────────────────────────────────
        createExperienceRequest: build.mutation({
            ...(USE_MOCKS
                ? m((body) => {
                    const cat = MOCK_CATEGORIES.find(c => c.id === body.categoryId) || MOCK_CATEGORIES[0]
                    const newReq = {
                        id: "req_" + Date.now(),
                        authorEmail: "me@belka.team",
                        authorName: "Me Me",
                        categoryId: cat.id,
                        categoryName: cat.name,
                        comment: body.comment,
                        items: body.items.map(i => ({ ...i, fullName: i.fullName || i.email })),
                        status: "WAITING",
                        createdAt: new Date().toISOString(),
                        adminEmail: null, adminName: null, adminComment: null, approvedAt: null,
                    }
                    mockState.requests.unshift(newReq)
                    return mockDelay(newReq)
                })
                : {
                    query: (body) => ({
                        url: "experience/requests",
                        method: "POST",
                        body,
                    }),
                }),
            invalidatesTags: ["ExperienceRequests"],
        }),
        claimReward: build.mutation({
            ...(USE_MOCKS
                ? m(() => {
                    const snap = buildSnapshot()
                    if (snap.currentLevel <= snap.lastClaimedLevel) {
                        return { error: { status: 400, data: { error: "No reward available" } } }
                    }
                    const target = MOCK_LEVELS.find(l => l.level === snap.lastClaimedLevel + 1)
                    if (!target) {
                        return { error: { status: 400, data: { error: "Level is not configured" } } }
                    }
                    mockState.lastClaimedLevel = target.level
                    mockState.transactions.unshift({
                        id: "tx_" + Date.now(),
                        email: "me@belka.team",
                        delta: 0,
                        type: "REWARD_CLAIM",
                        categoryId: null,
                        categoryName: null,
                        sourceRequestId: null,
                        rewardLevel: target.level,
                        comment: `XP reward, level ${target.level}`,
                        authorEmail: "SYSTEM",
                        authorName: "SYSTEM",
                        createdAt: new Date().toISOString(),
                    })
                    return mockDelay({
                        claimedLevel: target.level,
                        rewardBelcoins: target.rewardBelcoins,
                        balanceTransactionId: "bal_" + Date.now(),
                        experienceSnapshot: buildSnapshot(),
                    })
                })
                : {
                    query: () => ({
                        url: "experience/claim",
                        method: "POST",
                    }),
                }),
            invalidatesTags: ["Experience", "User"],
        }),

        // ── ADMIN queries ──────────────────────────────────────────────────
        getAllExperienceRequests: build.query({
            ...(USE_MOCKS
                ? m((params) => {
                    let list = [...mockState.requests]
                    if (params && params.status) list = list.filter(r => r.status === params.status)
                    if (params && params.authorEmail) list = list.filter(r => r.authorEmail === params.authorEmail)
                    list.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
                    return mockDelay(list)
                })
                : {
                    query: (params) => ({ url: "experience/requests/all", params }),
                }),
            providesTags: ["ExperienceRequests"],
        }),
        getAllExperienceBalances: build.query({
            ...(USE_MOCKS
                ? m(() => mockDelay([
                    buildSnapshot("ivan@belka.team"),
                    buildSnapshot("petr@belka.team"),
                    buildSnapshot("olga@belka.team"),
                ]))
                : {
                    query: (params) => ({ url: "experience/balances", params }),
                }),
            providesTags: ["Experience"],
        }),
        getEmployeeExperience: build.query({
            ...(USE_MOCKS
                ? m((email) => mockDelay(buildSnapshot(email)))
                : { query: (email) => `experience/${encodeURIComponent(email)}` }),
            providesTags: ["Experience"],
        }),
        getEmployeeExperienceTransactions: build.query({
            ...(USE_MOCKS
                ? m((email) => mockDelay(
                    [...mockState.transactions].map(t => ({ ...t, email: email || t.email }))
                ))
                : { query: (email) => `experience/${encodeURIComponent(email)}/transactions` }),
            providesTags: ["Experience"],
        }),
        getEmployeeAchievements: build.query({
            ...(USE_MOCKS
                ? m(() => mockDelay(mockState.achievements.filter(a => a.revokedAt === null)))
                : { query: (email) => `experience/${encodeURIComponent(email)}/achievements` }),
            providesTags: ["Achievements"],
        }),
        getAllExperienceCategories: build.query({
            ...(USE_MOCKS
                ? m(() => mockDelay(MOCK_CATEGORIES))
                : { query: () => "experience/categories/all" }),
            providesTags: ["ExperienceCategories"],
        }),
        // Total accrued XP per employee for a period, broken down by category.
        // dateFrom/dateTo — "dd.MM.yyyy" strings. Used via the lazy hook
        // (loaded on button click, not on mount).
        getExperienceSummary: build.query({
            query: ({ dateFrom, dateTo }) => ({
                url: "experience/transactions/summary",
                params: { dateFrom, dateTo },
            }),
        }),

        // ── ADMIN mutations ────────────────────────────────────────────────
        approveExperienceRequest: build.mutation({
            ...(USE_MOCKS
                ? m((body) => {
                    const req = mockState.requests.find(r => r.id === body.requestId)
                    if (!req) return { error: { status: 400, data: { error: "Request not found" } } }
                    if (req.status !== "WAITING") return { error: { status: 400, data: { error: "Request already processed" } } }
                    if (body.categoryId) {
                        const cat = MOCK_CATEGORIES.find(c => c.id === body.categoryId)
                        if (cat) { req.categoryId = cat.id; req.categoryName = cat.name }
                    }
                    req.status = "APPROVED"
                    req.adminEmail = "admin@belka.team"
                    req.adminName = "Admin"
                    req.adminComment = body.adminComment || ""
                    req.approvedAt = new Date().toISOString()
                    return mockDelay({ status: "ok", updatedEmails: req.items.map(i => i.email) })
                })
                : {
                    query: (body) => ({
                        url: "experience/requests/approve",
                        method: "POST",
                        body,
                    }),
                }),
            invalidatesTags: ["ExperienceRequests", "Experience", "Achievements"],
        }),
        rejectExperienceRequest: build.mutation({
            ...(USE_MOCKS
                ? m((body) => {
                    const req = mockState.requests.find(r => r.id === body.requestId)
                    if (!req) return { error: { status: 400, data: { error: "Request not found" } } }
                    if (req.status !== "WAITING") return { error: { status: 400, data: { error: "Request already processed" } } }
                    req.status = "REJECTED"
                    req.adminEmail = "admin@belka.team"
                    req.adminName = "Admin"
                    req.adminComment = body.adminComment || ""
                    req.approvedAt = new Date().toISOString()
                    return mockDelay({ status: "ok" })
                })
                : {
                    query: (body) => ({
                        url: "experience/requests/reject",
                        method: "POST",
                        body,
                    }),
                }),
            invalidatesTags: ["ExperienceRequests"],
        }),
        adjustExperience: build.mutation({
            ...(USE_MOCKS
                ? m(() => mockDelay(buildSnapshot()))
                : {
                    query: (body) => ({
                        url: "experience/adjust",
                        method: "POST",
                        body,
                    }),
                }),
            invalidatesTags: ["Experience", "Achievements"],
        }),

        // CRUD ExperienceCategory
        createExperienceCategory: build.mutation({
            ...(USE_MOCKS
                ? m((body) => mockDelay({ id: "cat_" + Date.now(), active: true, ...body }))
                : { query: (body) => ({ url: "experience/categories", method: "POST", body }) }),
            invalidatesTags: ["ExperienceCategories"],
        }),
        updateExperienceCategory: build.mutation({
            ...(USE_MOCKS
                ? m((body) => mockDelay({ ...body }))
                : { query: ({ id, ...body }) => ({ url: `experience/categories/${id}`, method: "PUT", body }) }),
            invalidatesTags: ["ExperienceCategories"],
        }),
        deleteExperienceCategory: build.mutation({
            ...(USE_MOCKS
                ? m(() => mockDelay({ status: "ok" }))
                : { query: (id) => ({ url: `experience/categories/${id}`, method: "DELETE" }) }),
            invalidatesTags: ["ExperienceCategories"],
        }),

        // CRUD ExperienceLevel
        createExperienceLevel: build.mutation({
            ...(USE_MOCKS
                ? m((body) => mockDelay({ id: "lvl_" + Date.now(), ...body }))
                : { query: (body) => ({ url: "experience/levels", method: "POST", body }) }),
            invalidatesTags: ["ExperienceLevels"],
        }),
        updateExperienceLevel: build.mutation({
            ...(USE_MOCKS
                ? m((body) => mockDelay({ ...body }))
                : { query: ({ id, ...body }) => ({ url: `experience/levels/${id}`, method: "PUT", body }) }),
            invalidatesTags: ["ExperienceLevels"],
        }),
        deleteExperienceLevel: build.mutation({
            ...(USE_MOCKS
                ? m(() => mockDelay({ status: "ok" }))
                : { query: (id) => ({ url: `experience/levels/${id}`, method: "DELETE" }) }),
            invalidatesTags: ["ExperienceLevels"],
        }),

        // CRUD AchievementCategory
        createAchievementCategory: build.mutation({
            ...(USE_MOCKS
                ? m((body) => mockDelay({ id: "ach_" + Date.now(), ...body }))
                : { query: (body) => ({ url: "experience/achievements/categories", method: "POST", body }) }),
            invalidatesTags: ["AchievementCategories"],
        }),
        updateAchievementCategory: build.mutation({
            ...(USE_MOCKS
                ? m((body) => mockDelay({ ...body }))
                : { query: ({ id, ...body }) => ({ url: `experience/achievements/categories/${id}`, method: "PUT", body }) }),
            invalidatesTags: ["AchievementCategories"],
        }),
        deleteAchievementCategory: build.mutation({
            ...(USE_MOCKS
                ? m(() => mockDelay({ status: "ok" }))
                : { query: (id) => ({ url: `experience/achievements/categories/${id}`, method: "DELETE" }) }),
            invalidatesTags: ["AchievementCategories"],
        }),

        grantAchievement: build.mutation({
            ...(USE_MOCKS
                ? m((body) => mockDelay({ status: "ok", ...body }))
                : { query: (body) => ({ url: "experience/achievements/grant", method: "POST", body }) }),
            invalidatesTags: ["Achievements"],
        }),
        revokeAchievement: build.mutation({
            ...(USE_MOCKS
                ? m(() => mockDelay({ status: "ok" }))
                : { query: (body) => ({ url: "experience/achievements/revoke", method: "POST", body }) }),
            invalidatesTags: ["Achievements"],
        }),
        recomputeAchievements: build.mutation({
            ...(USE_MOCKS
                ? m(() => mockDelay({ scannedEmployees: 42, grantedAchievements: 3 }))
                : { query: () => ({ url: "experience/achievements/recompute", method: "POST" }) }),
            invalidatesTags: ["Achievements"],
        }),

        // ── Seasons (Belka English Pass) ───────────────────────────────────
        // Активен ровно один сезон; переключение — только из админки.
        getActiveSeason: build.query({
            query: () => "experience/seasons/active",
            providesTags: ["Seasons"],
        }),
        // Результаты пользователя в завершённых сезонах — блок «Результаты
        // сезонов» на странице XP.
        getMySeasonResults: build.query({
            query: () => "experience/seasons/results",
            providesTags: ["SeasonResults"],
        }),
        getAllSeasons: build.query({
            query: () => "experience/seasons",
            providesTags: ["Seasons"],
        }),
        createSeason: build.mutation({
            query: (body) => ({ url: "experience/seasons", method: "POST", body }),
            invalidatesTags: ["Seasons"],
        }),
        // Предупреждения для диалога подтверждения активации (lazy, по клику).
        getSeasonActivationPreview: build.query({
            query: (number) => `experience/seasons/${number}/activation-preview`,
        }),
        // Активация меняет и счётчики, и весь сезонный контент — сбрасываем всё.
        // payoutPending=true → заодно выплатить unclaimed rewards закрываемого
        // сезона (чекбокс в диалоге подтверждения).
        activateSeason: build.mutation({
            query: ({ number, payoutPending }) => ({
                url: `experience/seasons/${number}/activate`,
                method: "POST",
                params: payoutPending ? { payoutPending: true } : undefined,
            }),
            invalidatesTags: [
                "Seasons", "SeasonResults", "Experience", "ExperienceCategories",
                "ExperienceLevels", "Mascots", "AchievementCategories", "Achievements", "User",
            ],
        }),
        // Добор упавших выплат закрытого сезона (payoutErrors > 0 после
        // активации). Идемпотентно на бэке — выплаченное не задваивается.
        payoutSeason: build.mutation({
            query: (number) => ({ url: `experience/seasons/${number}/payout`, method: "POST" }),
            invalidatesTags: ["Seasons", "Experience", "User"],
        }),
        // Уровни всех сезонов (в т.ч. подготовленные заранее) — для админки.
        getAllExperienceLevels: build.query({
            query: () => "experience/levels/all",
            providesTags: ["ExperienceLevels"],
        }),
    }),
})

export const {
    useGetExperienceQuery,
    useGetMyExperienceTransactionsQuery,
    useGetMyAchievementsQuery,
    useGetAllAchievementCategoriesQuery,
    useGetAchievementCategoriesAdminQuery,
    useGetExperienceCategoriesQuery,
    useGetExperienceLevelsQuery,
    useGetMyExperienceRequestsQuery,

    useGetMascotsQuery,
    useGetAllMascotsQuery,
    useCreateMascotMutation,
    useUpdateMascotMutation,
    useDeleteMascotMutation,

    useGetAllCouponsQuery,
    useCreateCouponMutation,
    useUpdateCouponMutation,
    useToggleCouponActiveMutation,
    useRedeemCouponMutation,

    useGetAppCopyQuery,
    useGetAllAppCopyQuery,
    useUpsertAppCopyMutation,
    useDeleteAppCopyMutation,

    useCreateExperienceRequestMutation,
    useClaimRewardMutation,

    useGetAllExperienceRequestsQuery,
    useGetAllExperienceBalancesQuery,
    useGetEmployeeExperienceQuery,
    useGetEmployeeExperienceTransactionsQuery,
    useGetEmployeeAchievementsQuery,
    useGetAllExperienceCategoriesQuery,
    useLazyGetExperienceSummaryQuery,

    useApproveExperienceRequestMutation,
    useRejectExperienceRequestMutation,
    useAdjustExperienceMutation,

    useCreateExperienceCategoryMutation,
    useUpdateExperienceCategoryMutation,
    useDeleteExperienceCategoryMutation,

    useCreateExperienceLevelMutation,
    useUpdateExperienceLevelMutation,
    useDeleteExperienceLevelMutation,

    useCreateAchievementCategoryMutation,
    useUpdateAchievementCategoryMutation,
    useDeleteAchievementCategoryMutation,

    useGrantAchievementMutation,
    useRevokeAchievementMutation,
    useRecomputeAchievementsMutation,

    useGetActiveSeasonQuery,
    useGetMySeasonResultsQuery,
    useGetAllSeasonsQuery,
    useCreateSeasonMutation,
    useLazyGetSeasonActivationPreviewQuery,
    useActivateSeasonMutation,
    usePayoutSeasonMutation,
    useGetAllExperienceLevelsQuery,
} = experienceApi
